"use client";

import { useTranslations } from "next-intl";
import {
  Armchair,
  Car,
  Clock,
  Hotel,
  MapPin,
  Phone,
  Plane,
  Wifi,
} from "lucide-react";
import { RegionalLayout } from "@/components/RegionalLayout";
import {
  RegionalHero,
  RegionalInfoStrip,
  SectionHeader,
} from "@/components/RegionalHome";
import { mpsmConfig } from "./config";

// Imágenes para el carrusel del hero
const CAROUSEL_IMAGES = [
  "https://galeria.tocumenaero.com/gallery/regionales/MPSM.jpg",
  "https://galeria.tocumenaero.com/gallery/regionales/MPSM2.jpg",
];

const SERVICE_ICONS = [Plane, Armchair, Wifi, Hotel] as const;

type Destino = {
  title: string;
  badge: string;
  desc: string;
};

type Service = {
  title: string;
  desc: string;
};

export default function MpsmPage() {
  const t = useTranslations("mpsm");

  const destinos = t.raw("destinos.items") as Destino[];
  const services = t.raw("servicios.items") as Service[];

  const infoItems = [
    {
      icon: MapPin,
      label: t("info.ubicacionLabel"),
      value: t("info.ubicacionValue"),
    },
    {
      icon: Clock,
      label: t("info.horarioLabel"),
      value: t("info.horarioValue"),
    },
    {
      icon: Phone,
      label: t("info.telefonoLabel"),
      value: mpsmConfig.contact.phone,
      href: `tel:${mpsmConfig.contact.phone}`,
    },
    {
      icon: Wifi,
      label: t("info.wifiLabel"),
      value: t("info.wifiValue"),
    },
  ];

  return (
    <RegionalLayout config={mpsmConfig}>
      <div className="bg-white">
        <RegionalHero
          images={CAROUSEL_IMAGES}
          eyebrow={t("hero.eyebrow")}
          title={t("hero.title")}
          subtitle={t("hero.subtitle")}
        />

        <RegionalInfoStrip items={infoItems} />

        {/* Destinos */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("destinos.eyebrow")}
              title={t("destinos.title")}
              subtitle={t("destinos.subtitle")}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {destinos.map((destino) => (
                <div
                  key={destino.title}
                  className="bg-white p-8 transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <Plane className="w-6 h-6 text-[#102542]" />
                    <span className="shrink-0 px-3 py-1 rounded-full border border-gray-300 text-xs uppercase tracking-wider font-semibold text-[#102542]">
                      {destino.badge}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight mb-2">
                    {destino.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {destino.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Servicios de la terminal */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("servicios.eyebrow")}
              title={t("servicios.title")}
              subtitle={t("servicios.subtitle")}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {services.map((service, index) => {
                const Icon = SERVICE_ICONS[index] ?? Plane;
                return (
                  <div
                    key={service.title}
                    className="bg-white p-8 transition-colors hover:bg-gray-50"
                  >
                    <Icon className="w-6 h-6 text-[#102542] mb-4" />
                    <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {service.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Instalaciones */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("instalaciones.eyebrow")}
              title={t("instalaciones.title")}
              subtitle={t("instalaciones.subtitle")}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {/* Estacionamiento */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <Car className="w-6 h-6 text-[#102542]" />
                  <span className="px-3 py-1 rounded-full border border-gray-300 text-xs uppercase tracking-wider font-semibold text-[#102542]">
                    {t("instalaciones.estacionamiento.badge")}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.estacionamiento.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("instalaciones.estacionamiento.desc")}
                </p>
              </div>

              {/* Transfer a hoteles */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <Hotel className="w-6 h-6 text-[#102542] mb-6" />
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.transfer.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("instalaciones.transfer.desc")}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </RegionalLayout>
  );
}
